import { createSlice } from '@reduxjs/toolkit';
import { sortBy, find } from 'lodash';
import moment from 'moment';
import {
  IN_ROOM_DINING,
  IN_ROOM_DINING_COUNT,
  SPA_SORTING_ORDER,
  SPA,
  DATE_FORMAT,
  RES_TABLE_STATUS,
  REST_SORTING_ORDER,
  HOUSEKEEPING_SORTING_ORDER,
  FNB_ORDERS_COUNT,
  FNB_SORTING_ORDER,
  FNB_ORDERS,
  FNB_BILL,
  HOUSEKEEPING,
  TABLE_RESERVATION,
} from '../../constants/DashboardConst';
import {
  fnbDashboardBillSubscription,
  fnbDashboardOrderSubscription,
  getDashboardFAndBOrderDetails,
  getHousekeepingOrderList,
  getOccupancyBookings,
  getOccupancyCheckins,
  getOccupancyOrderList,
  getOrderList,
  getRestaurantDashboardReservationDetails,
  getSpaOrderList,
  irdDashboardOrderSubscription,
  spaDashboardOrderSubscription,
  housekeepingOrderSubscription,
  restaurantTableDashboardSubscription,
} from '../../utils/dashboardAPIUtils';
import { getCurrentDayBooking, getTableStatus } from '../../utils/dashboardUtils';
import { decryptLocalStorage } from '../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../utils/loaderSlice';
import { notificationManager } from '../../utils/NotificationUtils';
import { logError } from '../../utils/commonUtils';
import { ENVIRONMENT } from '../../env/Configurations';

export const dahboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    dashboardIrdList: [],
    dashboardIRDNewOrder: null,
    dashboardIRDNotification: [],
    dashboardIrdCount: IN_ROOM_DINING_COUNT,
    dashboardFnbList: [],
    dashboardFnbNotification: [],
    dashboardFnbCount: FNB_ORDERS_COUNT,
    dashboardFnbLoading: false,
    dashboardFnbIrdMode: false,
    dashboardFnbBillsList: [],
    dashboardSpaList: [],
    dashboardViewState: {
      type: '',
      status: false,
      obj: null,
      previousObj: null,
    },
    dashboardFilterDate: {
      displayDate: '',
      formatDate: '',
      startDate: '',
    },
    dashboardCalendarType: 'timeGridDay',
    dashboardSpaNewOrde: null,
    restaurantDashboardReservations: [],
    restaurantDashboardReservationTable: [],
    restaurantDashboardReservationLoader: false,
    timings: [],
    housekeepingDashboardOrders: [],
    occupancyDashboardOrders: [],
    occupancyDashboardCheckins: [],
    occupancyDashboardBookings: [],
    restaurantId: '',
  },
  reducers: {
    setDashBoardIrdList: (state, action) => {
      state.dashboardIrdList = action.payload;
    },
    setNotification: (state, action) => {
      state.dashboardIRDNotification = action.payload;
    },
    setIRDDashboardCount: (state, action) => {
      state.dashboardIrdCount = action.payload;
    },
    setDashBoardFnbList: (state, action) => {
      state.dashboardFnbList = action.payload;
    },
    setDashBoardFnbLoading: (state, action) => {
      state.dashboardFnbLoading = action.payload;
    },
    setDashBoardFnbIrdMode: (state, action) => {
      state.dashboardFnbIrdMode = action.payload;
    },
    setFnbOrdersNotification: (state, action) => {
      state.dashboardFnbNotification = action.payload;
    },
    setFnbDashboardCount: (state, action) => {
      state.dashboardFnbCount = action.payload;
    },
    setFnbDashboardBillsList: (state, action) => {
      state.dashboardFnbBillsList = action.payload;
    },
    setDashBoardSpaList: (state, action) => {
      state.dashboardSpaList = action.payload;
    },
    setDashboardViewState: (state, action) => {
      state.dashboardViewState = action.payload;
    },
    setDashboardFilterDate: (state, action) => {
      state.dashboardFilterDate = action.payload;
    },
    setDashboardCalendarType: (state, action) => {
      state.dashboardCalendarType = action.payload;
    },
    setDashboardNewOrder: (state, action) => {
      state.dashboardNewOrder = action.payload;
    },
    setRestaurantDashboardReservations: (state, action) => {
      state.restaurantDashboardReservations = action.payload;
    },
    setRestaurantDashboardReservationTable: (state, action) => {
      state.restaurantDashboardReservationTable = action.payload;
    },
    setRestaurantDashboardReservationLoader: (state, action) => {
      state.restaurantDashboardReservationLoader = action.payload;
    },
    setTimings: (state, action) => {
      state.timings = action.payload;
    },
    setHousekeepingDashBoardOrders: (state, action) => {
      state.housekeepingDashboardOrders = action.payload;
    },
    setOccupancyDashBoardOrders: (state, action) => {
      state.occupancyDashboardOrders = action.payload;
    },
    setOccupancyDashBoardCheckins: (state, action) => {
      state.occupancyDashboardCheckins = action.payload;
    },
    setOccupancyDashboardBookings: (state, action) => {
      state.occupancyDashboardBookings = action.payload;
    },
    setRestaurantId: (state, action) => {
      state.restaurantId = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setDashBoardIrdList,
  setNotification,
  setIRDDashboardCount,
  setDashBoardFnbList,
  setDashBoardFnbLoading,
  setDashBoardFnbIrdMode,
  setFnbOrdersNotification,
  setFnbDashboardCount,
  setFnbDashboardBillsList,
  setDashBoardSpaList,
  setDashboardViewState,
  setDashboardFilterDate,
  setDashboardCalendarType,
  setDashboardNewOrder,
  setRestaurantDashboardReservations,
  setRestaurantDashboardReservationTable,
  setRestaurantDashboardReservationLoader,
  setTimings,
  setHousekeepingDashBoardOrders,
  setOccupancyDashBoardOrders,
  setOccupancyDashBoardCheckins,
  setOccupancyDashboardBookings,
  setRestaurantId,
} = dahboardSlice.actions;

// ------------------------------------------------------------------------------------------------------------------------ //
// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(setAllGroups([{}, {}]))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched

// Fetch all In Room Dining related details and set to global store
export const fetchAllDashboardIrdList = (hotelId) => async (dispatch) => {
  dispatch(setDashBoardFnbLoading(true));

  try {
    dispatch(stepUpLoaderCount());

    const selectedLanguage = decryptLocalStorage('lang');
    const language = selectedLanguage !== 'en' ? selectedLanguage : '';
    const res = await getOrderList(hotelId, language);
    const irdList = res.data.getOrdersDetails.map((order) => {
      let orderStatus = '',
        statusClass = '';
      if (order.status === 'ACCEPTED') {
        orderStatus = 'Preparing';
        statusClass = 'pill-preparing';
      } else if (order.status === 'NEW_ORDER') {
        orderStatus = 'New Order';
        statusClass = 'pill-new';
      } else if (order.status === 'COMPLETED') {
        orderStatus = 'Completed';
        statusClass = 'pill-completed';
      } else if (order.status === 'DELAYED') {
        orderStatus = 'Delayed';
        statusClass = 'pill-delayed';
      }
      return { ...order, orderStatus, statusClass };
    });

    const notCompleted = irdList.filter((item) => item.status !== 'COMPLETED');
    const sortedList = sortBy(notCompleted, [(status) => status.orderStatus.toLowerCase()]);
    const completed = irdList.filter((item) => item.status === 'COMPLETED');
    sortedList.push(...completed);
    dispatch(setDashBoardIrdList(sortedList));

    const getPreparingCount = res.data.getOrdersDetails?.filter(
      (irdDashboard) => irdDashboard.status === 'ACCEPTED',
    );
    const getNewOrderCount = res.data.getOrdersDetails?.filter(
      (irdDashboard) => irdDashboard.status === 'NEW_ORDER',
    );
    const getCompletedCount = res.data.getOrdersDetails?.filter(
      (irdDashboard) => irdDashboard.status === 'COMPLETED',
    );
    const getDelayedCount = res.data.getOrdersDetails?.filter(
      (irdDashboard) => irdDashboard.status === 'DELAYED',
    );

    const irdDashboarCount = IN_ROOM_DINING_COUNT.map((countValue) => {
      let count = 0;
      switch (countValue.name) {
        case 'Total Orders Received':
          count =
            getPreparingCount.length +
            getNewOrderCount.length +
            getCompletedCount.length +
            getDelayedCount.length;
          break;
        case 'New Orders':
          count = getNewOrderCount.length;
          break;
        case 'Preparing':
          count = getPreparingCount.length;
          break;
        case 'Delayed Orders':
          count = getDelayedCount.length;
          break;
        case 'Completed':
          count = getCompletedCount.length;
          break;
        default:
          break;
      }
      return { ...countValue, count };
    });
    dispatch(setDashBoardFnbIrdMode(true));
    dispatch(setIRDDashboardCount(irdDashboarCount));
    dispatch(setDashBoardFnbLoading(false));
  } catch (error) {
    dispatch(setDashBoardFnbLoading(false));

    logError(hotelId, 'fetchAllDashboardIrdList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchIRDDashboardOrderSubscription =
  (hotelId, notification) => async (dispatch, getState) => {
    try {
      dispatch(stepUpLoaderCount());
      const subscription = (await irdDashboardOrderSubscription(hotelId)).subscribe({
        next: (data) => {
          if (
            decryptLocalStorage('hotelId') === hotelId &&
            decryptLocalStorage('restaurantId') === ''
          ) {
            if (hotelId !== data?.data?.onCreateOrder.hotelId) return false;
            dispatch(fetchAllDashboardIrdList(hotelId));
            const currNotification = [...notification];
            currNotification.push({
              open: true,
              title: 'New Order',
              description: `Room ${data?.data?.onCreateOrder?.roomNo} - ${(
                '0' + data?.data?.onCreateOrder?.items?.length
              ).slice(-2)} Item${data?.data?.onCreateOrder?.items?.length > 1 ? 's' : ''}`,
              buttonTitle: 'View Order',
              type: IN_ROOM_DINING,
            });
            dispatch(setNotification(currNotification));
            dispatch(setDashboardNewOrder(data?.data?.onCreateOrder));
          } else {
            subscription.unsubscribe();
          }
        },
      });
    } catch (error) {
      logError(hotelId, 'fetchIRDDashboardOrderSubscription', error?.message);

      if (ENVIRONMENT === 'dev') {
        console.log(error?.message);
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

export const fetchFnBDashboardOrderSubscription =
  (hotelId, restaurantId, notification) => async (dispatch, getState) => {
    try {
      dispatch(stepUpLoaderCount());
      const subscription = (await fnbDashboardOrderSubscription(hotelId, restaurantId)).subscribe({
        next: (data) => {
          if (
            decryptLocalStorage('hotelId') === hotelId &&
            decryptLocalStorage('restaurantId') === restaurantId
          ) {
            if (hotelId !== data?.data?.onCreateFAndBOrder.hotelId) return false;
            dispatch(
              fetchAllDashboardFnbList(hotelId, data?.data?.onCreateFAndBOrder.restaurantId),
            );
            const currNotification = [...notification];
            currNotification.push({
              open: true,
              title: 'New Order',
              description: `Table ${data?.data?.onCreateFAndBOrder?.tableNumber} - ${(
                '0' + data?.data?.onCreateFAndBOrder?.items?.length
              ).slice(-2)} Item${data?.data?.onCreateFAndBOrder?.items?.length > 1 ? 's' : ''}`,
              buttonTitle: 'View Order',
              type: FNB_ORDERS,
            });
            dispatch(setNotification(currNotification));
            dispatch(setDashboardNewOrder(data?.data?.onCreateFAndBOrder));
          } else {
            subscription.unsubscribe();
          }
        },
      });
    } catch (error) {
      logError(hotelId, 'fetchFnBDashboardOrderSubscription', error?.message);

      if (ENVIRONMENT === 'dev') {
        console.log(error?.message);
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

export const fetchFnBDashboardBillSubscription =
  (hotelId, restaurantId, notification) => async (dispatch, getState) => {
    try {
      dispatch(stepUpLoaderCount());
      const subscription = (await fnbDashboardBillSubscription(hotelId, restaurantId)).subscribe({
        next: (data) => {
          if (
            decryptLocalStorage('hotelId') === hotelId &&
            decryptLocalStorage('restaurantId') === restaurantId
          ) {
            if (hotelId !== data?.data?.onRequestFAndBBill.hotelId) return false;
            dispatch(
              fetchAllDashboardFnbList(hotelId, data?.data?.onRequestFAndBBill.restaurantId),
            );
            const currNotification = [...notification];
            currNotification.push({
              open: true,
              title: 'Bill Requested',
              description: `Table ${data?.data?.onRequestFAndBBill?.tableNumber}`,
              buttonTitle: 'View Bill',
              type: FNB_BILL,
            });
            dispatch(setNotification(currNotification));
            dispatch(setDashboardNewOrder(data?.data?.onRequestFAndBBill));
          } else {
            subscription.unsubscribe();
          }
        },
      });
    } catch (error) {
      logError(hotelId, 'fetchFnBDashboardBillSubscription', error?.message);

      if (ENVIRONMENT === 'dev') {
        console.log(error?.message);
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

export const fetchRestaurantTableDashboardSubscription =
  (hotelId, restaurantId, notification) => async (dispatch, getState) => {
    try {
      dispatch(stepUpLoaderCount());
      const subscription = (
        await restaurantTableDashboardSubscription(hotelId, restaurantId)
      ).subscribe({
        next: (data) => {
          if (
            decryptLocalStorage('hotelId') === hotelId &&
            decryptLocalStorage('restaurantId') === restaurantId
          ) {
            if (hotelId !== data?.data?.onCreateRestaurantReservation.hotelId) return false;
            dispatch(
              fetchRestaurantDashboardReservationDetails(
                data?.data?.onCreateRestaurantReservation.restaurantId,
                hotelId,
              ),
            );
            const currNotification = [...notification];
            currNotification.push({
              open: true,
              title: 'New Table Reservation',
              description: `Table ${
                data?.data?.onCreateRestaurantReservation?.tableNumbers?.length > 0
                  ? data?.data?.onCreateRestaurantReservation?.tableNumbers[0]
                  : 0
              }`,
              buttonTitle: 'View Table Reservation',
              type: TABLE_RESERVATION,
            });
            dispatch(setNotification(currNotification));
            dispatch(setDashboardNewOrder(data?.data?.onCreateRestaurantReservation));
          } else {
            subscription.unsubscribe();
          }
        },
      });
    } catch (error) {
      logError(hotelId, 'fetchRestaurantTableDashboardSubscription', error?.message);

      if (ENVIRONMENT === 'dev') {
        console.log(error?.message);
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

export const fetchHousekeepingOrderSubscription =
  (hotelId, notification) => async (dispatch, getState) => {
    try {
      dispatch(stepUpLoaderCount());
      const subscription = (await housekeepingOrderSubscription()).subscribe({
        next: (data) => {
          if (decryptLocalStorage('hotelId') === hotelId) {
            if (hotelId !== data?.data?.onCreateHouseKeepingOrder.hotelId) return false;
            dispatch(fetchHousekeepingOrdersList(hotelId));
            const currNotification = [...notification];
            currNotification.push({
              open: true,
              title: 'New Order',
              description: `Room ${data?.data?.onCreateHouseKeepingOrder?.roomNo} - ${data?.data?.onCreateHouseKeepingOrder?.serviceName}`,
              buttonTitle: 'View Order',
              type: HOUSEKEEPING,
            });
            dispatch(setNotification(currNotification));
            dispatch(setDashboardNewOrder(data?.data?.onCreateHouseKeepingOrder));
          } else {
            subscription.unsubscribe();
          }
        },
      });
    } catch (error) {
      logError(hotelId, 'fetchHousekeepingOrderSubscription', error?.message);

      if (ENVIRONMENT === 'dev') {
        console.log(error?.message);
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

export const fetchAllDashboardFnbList = (hotelId, restaurantId) => async (dispatch) => {
  dispatch(setDashBoardFnbLoading(true));
  try {
    dispatch(stepUpLoaderCount());
    const selectedLanguage = decryptLocalStorage('lang');
    const language = selectedLanguage !== 'en' ? selectedLanguage : '';
    if (restaurantId) {
      const res = await getDashboardFAndBOrderDetails(hotelId, restaurantId, language);

      const billList = res.data.getFAndBOrderDetails.bills.map((order) => {
        let orderStatus = '',
          statusClass = '';
        if (order.status === 'BILL_REQUESTED') {
          orderStatus = 'Bill Requested';
          statusClass = 'pill-bill-requested';
        } else if (order.status === 'PAID') {
          orderStatus = 'Paid';
          statusClass = 'pill-paid';
        }
        return { ...order, orderStatus, statusClass };
      });
      billList.sort(function (a, b) {
        return FNB_SORTING_ORDER.indexOf(a.status) - FNB_SORTING_ORDER.indexOf(b.status);
      });
      dispatch(setDashBoardFnbIrdMode(false));
      dispatch(setFnbDashboardBillsList(billList));

      const ordersList = res.data.getFAndBOrderDetails.orders.map((order) => {
        let orderStatus = '',
          statusClass = '';
        if (order.status === 'PREPARING') {
          orderStatus = 'Preparing';
          statusClass = 'pill-preparing';
        } else if (order.status === 'NEW_ORDER') {
          orderStatus = 'New Order';
          statusClass = 'pill-new';
        } else if (order.status === 'DELIVERED') {
          orderStatus = 'Delivered';
          statusClass = 'pill-completed';
        } else if (order.status === 'DECLINED') {
          orderStatus = 'Declined';
          statusClass = 'pill-declined';
        } else if (order.status === 'BILL_REQUESTED') {
          orderStatus = 'Bill Requested';
          statusClass = 'pill-bill-requested';
        } else if (order.status === 'PAID') {
          orderStatus = 'Paid';
          statusClass = 'pill-paid';
        }
        return { ...order, orderStatus, statusClass };
      });

      ordersList.sort(function (a, b) {
        return FNB_SORTING_ORDER.indexOf(a.status) - FNB_SORTING_ORDER.indexOf(b.status);
      });
      dispatch(setDashBoardFnbList(ordersList));
      const fnbDashboarCount = FNB_ORDERS_COUNT.map((countValue) => {
        const count =
          countValue.status === 'TOTAL'
            ? ordersList.length
            : countValue.status === 'BILL_REQUESTED'
            ? billList.filter((item) => item.status === 'BILL_REQUESTED').length
            : ordersList.filter((item) => item.status === countValue.status).length;

        return { ...countValue, count };
      });
      dispatch(setFnbDashboardCount(fnbDashboarCount));
      dispatch(setDashBoardFnbLoading(false));
    }
  } catch (error) {
    logError(hotelId, 'fetchAllDashboardFnbList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    dispatch(setDashBoardFnbLoading(false));
    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchSpaDashboardOrderList = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const selectedLanguage = decryptLocalStorage('lang');
    const language = selectedLanguage !== 'en' ? selectedLanguage : '';
    const res = await getSpaOrderList(hotelId, language);
    const spaOrderListSorted = sortBy(res.data.getSpaOrdersDetails, [
      (spaOrder) => spaOrder.guestName.toLowerCase(),
    ]);

    const spaOrderListUpdatedStatus = spaOrderListSorted.map((spaOrder) => {
      let orderStatus = '';
      let statusClass = '';
      let status = '';
      switch (spaOrder.status) {
        case 'NEW_REQUEST':
          orderStatus = 'New Request';
          statusClass = 'spa-pill-new-request';
          status = spaOrder.status;
          break;
        case 'ACCEPTED':
          if (
            moment().format('YYYY-MM-DD') ===
            moment(spaOrder.scheduledDate, DATE_FORMAT).format('YYYY-MM-DD')
          ) {
            orderStatus = 'Arriving Today';
            statusClass = 'spa-pill-arriving-today';
            status = 'ARRIVING_TODAY';
          } else {
            orderStatus = 'Scheduled';
            statusClass = 'spa-pill-scheduled';
            status = 'SCHEDULED';
          }
          break;
        case 'DECLINED':
          orderStatus = 'Declined';
          statusClass = 'spa-pill-declined';
          status = spaOrder.status;
          break;
        case 'GUEST_DECLINED':
          orderStatus = 'Cancelled';
          statusClass = 'spa-pill-cancelled';
          status = spaOrder.status;
          break;
        case 'COMPLETED':
          orderStatus = 'Completed';
          statusClass = 'spa-pill-completed';
          status = spaOrder.status;
          break;
        default:
          break;
      }
      return { ...spaOrder, orderStatus, statusClass, status };
    });

    // THIS IS TO SORT THE INCOMING REQUESTS IN THE INTENDED ORDER
    spaOrderListUpdatedStatus.sort(function (a, b) {
      return SPA_SORTING_ORDER.indexOf(a.status) - SPA_SORTING_ORDER.indexOf(b.status);
    });
    dispatch(setDashBoardSpaList(spaOrderListUpdatedStatus));
  } catch (error) {
    logError(hotelId, 'fetchSpaDashboardOrderList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchSpaDashboardOrderSubscription =
  (hotelId, spaId, notification) => async (dispatch, getState) => {
    try {
      dispatch(stepUpLoaderCount());
      const subscription = (await spaDashboardOrderSubscription(hotelId, spaId)).subscribe({
        next: (data) => {
          if (
            decryptLocalStorage('hotelId') === hotelId &&
            decryptLocalStorage('spaId') === spaId
          ) {
            if (hotelId !== data?.data?.onCreateSpaOrder.hotelId) return false;
            dispatch(fetchSpaDashboardOrderList(hotelId));
            const currNotification = [...notification];
            currNotification.push({
              open: true,
              title: 'New Request',
              description: `${data?.data?.onCreateSpaOrder.guestName}`,
              buttonTitle: 'View Request',
              type: SPA,
            });
            dispatch(setNotification(currNotification));
            dispatch(setDashboardNewOrder(data?.data?.onCreateSpaOrder));
          } else {
            subscription.unsubscribe();
          }
        },
      });
    } catch (error) {
      logError(hotelId, 'fetchSpaDashboardOrderSubscription', error?.message);

      if (ENVIRONMENT === 'dev') {
        console.log(error?.message);
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

export const fetchRestaurantDashboardReservationDetails = (restaurantId, hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const selectedLanguage = decryptLocalStorage('lang');
    const language = selectedLanguage !== 'en' ? selectedLanguage : '';
    dispatch(setRestaurantDashboardReservationLoader(true));
    const restaurantDashboardReservation = await getRestaurantDashboardReservationDetails(
      restaurantId,
      hotelId,
      language,
    );
    let reservations = [];
    let tables = [];
    //info: Removed as total Reservations count was not getting displayed
    // if (
    //   restaurantDashboardReservation?.data?.getRestaurantReservationDetails
    //     ?.tables.length === 0
    // ) {
    //   reservations = [];
    //   tables = [];
    // } else {
    {
      const reservationList =
        restaurantDashboardReservation?.data?.getRestaurantReservationDetails?.reservations.map(
          (reservations) => {
            let orderStatus = '';
            let statusClass = '';
            let status = '';
            switch (reservations.status) {
              case 'New Request':
                orderStatus = 'New Request';
                statusClass = 'resto-pill-new-request';
                status = reservations.status;
                break;
              case 'Scheduled':
                if (
                  moment().format('YYYY-MM-DD') ===
                  moment(reservations.date, DATE_FORMAT).format('YYYY-MM-DD')
                ) {
                  orderStatus = 'Arriving Today';
                  statusClass = 'resto-pill-arriving-today';
                  status = 'Arriving Today';
                } else {
                  orderStatus = 'Scheduled';
                  statusClass = 'resto-pill-scheduled';
                  status = reservations.status;
                }
                break;
              case 'NoShow':
                orderStatus = 'No Show';
                statusClass = 'resto-pill-no-show';
                status = reservations.status;
                break;
              case 'Declined':
                orderStatus = 'Declined';
                statusClass = 'resto-pill-declined';
                status = reservations.status;
                break;
              case 'Cancelled':
                orderStatus = 'Cancelled';
                statusClass = 'resto-pill-cancelled';
                status = reservations.status;
                break;
              case 'Completed':
                orderStatus = 'Completed';
                statusClass = 'resto-pill-completed';
                status = reservations.status;
                break;
              default:
                break;
            }
            return { ...reservations, orderStatus, statusClass, status };
          },
        );
      reservations = sortBy(reservationList, (data) => find(data, data.tableNumbers));

      const tableList =
        restaurantDashboardReservation?.data?.getRestaurantReservationDetails.tables.map(
          (resTable) => {
            const tableStatus = getTableStatus(
              resTable,
              reservations,
              moment().format('YYYY-MM-DD'),
            );
            const currentDayBooking = getCurrentDayBooking(resTable.reservations);
            return {
              ...resTable,
              className: tableStatus.className,
              status: tableStatus.status,
              bookingCount: currentDayBooking,
            };
          },
        );
      tables = tableList.sort(function (a, b) {
        return (
          RES_TABLE_STATUS.findIndex((s) => s.name === a.status) -
          RES_TABLE_STATUS.findIndex((s) => s.name === b.status)
        );
      });
    }
    const sortedReservations = [...reservations].sort(function (a, b) {
      return REST_SORTING_ORDER.indexOf(a.status) - REST_SORTING_ORDER.indexOf(b.status);
    });
    dispatch(setRestaurantDashboardReservations(sortedReservations));
    dispatch(setRestaurantDashboardReservationTable(tables));
    dispatch(setRestaurantDashboardReservationLoader(false));
  } catch (error) {
    dispatch(setRestaurantDashboardReservations([]));
    dispatch(setRestaurantDashboardReservationTable([]));
    dispatch(setRestaurantDashboardReservationLoader(false));

    logError(null, 'fetchRestaurantDashboardReservationDetails', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchHousekeepingOrdersList = (hotelId, languageSelected) => async (dispatch) => {
  const lang = decryptLocalStorage('lang');
  const getByLang = lang !== 'en' ? lang : '';
  try {
    dispatch(stepUpLoaderCount());
    const res = await getHousekeepingOrderList(hotelId, getByLang);
    const ordersListSorted = sortBy(res.data.getHouseKeepingOrdersDetails, [
      (order) => order.guestName.toLowerCase(),
    ]);

    const orderListUpdatedStatus = ordersListSorted.map((order) => {
      let orderStatus = '';
      let statusClass = '';
      let status = '';
      switch (order.status) {
        case 'NEW_REQUEST':
          orderStatus = 'New Request';
          statusClass = 'hk-pill-new-request';
          status = order.status;
          break;
        case 'ASSIGNED':
          orderStatus = 'Assigned';
          statusClass = 'hk-pill-assigned';
          status = order.status;
          break;
        case 'ON_HOLD':
          orderStatus = 'On Hold';
          statusClass = 'hk-pill-onhold';
          status = order.status;
          break;
        case 'RESOLVED':
          orderStatus = 'Resolved';
          statusClass = 'hk-pill-resolved';
          status = order.status;
          break;
        default:
          break;
      }
      return { ...order, orderStatus, statusClass, status };
    });

    // THIS IS TO SORT THE INCOMING REQUESTS IN THE INTENDED ORDER
    orderListUpdatedStatus.sort(function (a, b) {
      return (
        HOUSEKEEPING_SORTING_ORDER.indexOf(a.status) - HOUSEKEEPING_SORTING_ORDER.indexOf(b.status)
      );
    });
    dispatch(setHousekeepingDashBoardOrders(orderListUpdatedStatus));
  } catch (error) {
    logError(hotelId, 'fetchHousekeepingOrdersList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchOccupancyOrdersList = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getOccupancyOrderList(hotelId);
    const ordersListSorted = res.data.getHouseStatus;
    dispatch(setOccupancyDashBoardOrders(ordersListSorted));
  } catch (error) {
    logError(hotelId, 'fetchOccupancyOrdersList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchOccupancyCheckinList = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getOccupancyCheckins(hotelId);
    const checkins = res.data.getTotalCheckIn;
    dispatch(setOccupancyDashBoardCheckins(checkins));
  } catch (error) {
    logError(hotelId, 'fetchOccupancyCheckinList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchOccupancyBookings = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const date = moment().format('YYYY-MM-DD');
    const res = await getOccupancyBookings(hotelId, date);
    const checkins = res.data.getFutureBookingDashboard;
    dispatch(setOccupancyDashboardBookings(checkins));
  } catch (error) {
    logError(hotelId, 'fetchOccupancyBookings', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};
// ------------------------------------------------------------------------------------------------------------------------ //

export const dashboardIrdListSelector = (state) => state.dashboard.dashboardIrdList;
export const dashboardIRDNotificationSelector = (state) => state.dashboard.dashboardIRDNotification;
export const dashboardIrdCountSelector = (state) => state.dashboard.dashboardIrdCount;
export const dashboardIRDNewOrderSelector = (state) => state.dashboard.dashboardIRDNewOrder;
export const dashboardFnbListSelector = (state) => state.dashboard.dashboardFnbList;
export const dashboardFnbLoadingSelector = (state) => state.dashboard.dashboardFnbLoading;
export const dashboardFnbIrdModeSelector = (state) => state.dashboard.dashboardFnbIrdMode;
export const dashboardFnbNotificationSelector = (state) => state.dashboard.dashboardFnbNotification;
export const dashboardFnbCountSelector = (state) => state.dashboard.dashboardFnbCount;
export const dashboardFnbBillsListSelector = (state) => state.dashboard.dashboardFnbBillsList;
export const dashboardSpaListSelector = (state) => state.dashboard.dashboardSpaList;
export const dashboardViewStateSelector = (state) => state.dashboard.dashboardViewState;
export const dashboardFilterDateSelector = (state) => state.dashboard.dashboardFilterDate;
export const dashboardCalendarTypeSelector = (state) => state.dashboard.dashboardCalendarType;
export const dashboardNewOrderSelector = (state) => state.dashboard.dashboardNewOrder;
export const restaurantDashboardReservationsSelector = (state) =>
  state.dashboard.restaurantDashboardReservations;
export const restaurantDashboardReservationTableSelector = (state) =>
  state.dashboard.restaurantDashboardReservationTable;
export const restaurantDashboardReservationLoaderSelector = (state) =>
  state.dashboard.restaurantDashboardReservationLoader;
export const restaurantTimingsSelector = (state) => state.dashboard.timings;
export const housekeepingOrdersSelector = (state) => state.dashboard.housekeepingDashboardOrders;
export const occuppancyOrdersSelector = (state) => state.dashboard.occupancyDashboardOrders;
export const occuppancyCheckinSelector = (state) => state.dashboard.occupancyDashboardCheckins;
export const occuppancyBookingSelector = (state) => state.dashboard.occupancyDashboardBookings;
export const getRestaurantId = (state) => state.dashboard.restaurantId;

export default dahboardSlice.reducer;
